import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Vendor, VendorCreate, VendorUpdate, VendorReservation } from '../types';
import { vendorApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';

interface VendorContextType {
  // State
  vendors: Vendor[];
  currentVendor: Vendor | null;
  reservations: VendorReservation[];
  isLoading: boolean;
  error: string | null;

  // Actions
  loadVendors: () => Promise<void>;
  loadVendor: (vendorId: number) => Promise<void>;
  createVendor: (data: VendorCreate) => Promise<Vendor>;
  updateVendor: (vendorId: number, data: VendorUpdate) => Promise<Vendor>;
  deactivateVendor: (vendorId: number) => Promise<void>;
  uploadPortfolioImage: (vendorId: number, file: File) => Promise<string>;
  removePortfolioImage: (vendorId: number, imageUrl: string) => Promise<void>;
  loadReservations: (vendorId: number) => Promise<void>;
  setCurrentVendor: (vendor: Vendor | null) => void;
  clearError: () => void;
}

const VendorContext = createContext<VendorContextType | null>(null);

// Export VendorContext as named export for components that need direct context access
export { VendorContext };

interface VendorProviderProps {
  children: ReactNode;
}

export function VendorProvider({ children }: VendorProviderProps) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [currentVendor, setCurrentVendor] = useState<Vendor | null>(null);
  const [reservations, setReservations] = useState<VendorReservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadVendors = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await vendorApi.getMyVendors();
      setVendors(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to load vendors:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadVendor = useCallback(async (vendorId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await vendorApi.getMyVendor(vendorId);
      setCurrentVendor(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to load vendor:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createVendor = useCallback(async (data: VendorCreate): Promise<Vendor> => {
    try {
      setIsLoading(true);
      setError(null);
      const newVendor = await vendorApi.createVendor(data);
      setVendors(prev => [...prev, newVendor]);
      return newVendor;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateVendor = useCallback(async (vendorId: number, data: VendorUpdate): Promise<Vendor> => {
    try {
      setIsLoading(true);
      setError(null);
      const updatedVendor = await vendorApi.updateVendor(vendorId, data);
      setVendors(prev => prev.map(v => v.id === vendorId ? updatedVendor : v));
      if (currentVendor?.id === vendorId) {
        setCurrentVendor(updatedVendor);
      }
      return updatedVendor;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentVendor]);

  const deactivateVendor = useCallback(async (vendorId: number): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await vendorApi.deactivateVendor(vendorId);
      setVendors(prev => prev.filter(v => v.id !== vendorId));
      if (currentVendor?.id === vendorId) {
        setCurrentVendor(null);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentVendor]);

  const uploadPortfolioImage = useCallback(async (vendorId: number, file: File): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await vendorApi.uploadPortfolioImage(vendorId, file);
      // Refresh vendor to get updated portfolio_images
      await loadVendor(vendorId);
      return response.image_url;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [loadVendor]);

  const removePortfolioImage = useCallback(async (vendorId: number, imageUrl: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await vendorApi.removePortfolioImage(vendorId, imageUrl);
      // Update local state
      if (currentVendor?.id === vendorId && currentVendor.portfolio_images) {
        setCurrentVendor({
          ...currentVendor,
          portfolio_images: currentVendor.portfolio_images.filter(url => url !== imageUrl)
        });
      }
      setVendors(prev => prev.map(v => {
        if (v.id === vendorId && v.portfolio_images) {
          return { ...v, portfolio_images: v.portfolio_images.filter(url => url !== imageUrl) };
        }
        return v;
      }));
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentVendor]);

  const loadReservations = useCallback(async (vendorId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await vendorApi.getVendorReservations(vendorId);
      setReservations(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to load reservations:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: VendorContextType = {
    vendors,
    currentVendor,
    reservations,
    isLoading,
    error,
    loadVendors,
    loadVendor,
    createVendor,
    updateVendor,
    deactivateVendor,
    uploadPortfolioImage,
    removePortfolioImage,
    loadReservations,
    setCurrentVendor,
    clearError,
  };

  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useVendor(): VendorContextType {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
}

export default VendorContext;
